interface OrnamentProps {
  className?: string;
}

/**
 * Eight-petal lotus mandala — concentric rings. Used as a single large
 * meditative ornament behind contemplative copy (e.g. Mission belief).
 */
export function LotusMandala({ className }: OrnamentProps) {
  const outer = [0, 45, 90, 135, 180, 225, 270, 315];
  const inner = [22.5, 67.5, 112.5, 157.5, 202.5, 247.5, 292.5, 337.5];
  return (
    <svg
      viewBox="-100 -100 200 200"
      className={className}
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g
        fill="none"
        stroke="currentColor"
        strokeWidth="1.1"
        strokeLinejoin="round"
      >
        {outer.map((a) => (
          <ellipse
            key={`outer-${a}`}
            cx="0"
            cy="-58"
            rx="13"
            ry="40"
            transform={`rotate(${a})`}
          />
        ))}
        {inner.map((a) => (
          <ellipse
            key={`inner-${a}`}
            cx="0"
            cy="-30"
            rx="7"
            ry="20"
            transform={`rotate(${a})`}
          />
        ))}
        <circle cx="0" cy="0" r="22" />
        <circle cx="0" cy="0" r="14" />
        <circle cx="0" cy="0" r="6" />
      </g>
      <circle cx="0" cy="0" r="2" fill="currentColor" />
    </svg>
  );
}

/**
 * Dotted journey-path ornament — curving lines connecting locations.
 * Used in the Centers section to suggest the geographic arc between
 * Varanasi → Holapur → Raipur → Mumbai → Nairobi.
 */
export function JourneyPath({ className }: OrnamentProps) {
  return (
    <svg
      viewBox="0 0 1400 700"
      className={className}
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeDasharray="1 9"
      >
        <path d="M 80 540 Q 240 380, 420 480 T 760 360 T 1080 220 T 1340 140" />
        <path d="M 120 220 Q 380 120, 620 180 T 1080 480" opacity="0.55" />
      </g>
      <g fill="currentColor">
        <circle cx="80" cy="540" r="3.5" />
        <circle cx="420" cy="480" r="2.5" />
        <circle cx="760" cy="360" r="3" />
        <circle cx="1080" cy="220" r="2.5" />
        <circle cx="1340" cy="140" r="3.5" />
      </g>
    </svg>
  );
}

/**
 * Seed constellation — sparse dot cluster with linking lines.
 * Used in Team header area as a small corner accent.
 */
export function SeedConstellation({ className }: OrnamentProps) {
  const seeds: Array<[number, number, number]> = [
    [22, 32, 2.6],
    [62, 52, 1.4],
    [96, 26, 1.8],
    [136, 78, 1.2],
    [180, 42, 2.4],
    [220, 88, 1.4],
    [70, 96, 1.6],
    [156, 18, 1.2],
  ];
  return (
    <svg
      viewBox="0 0 250 120"
      className={className}
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g
        fill="none"
        stroke="currentColor"
        strokeWidth="0.7"
        strokeLinecap="round"
        opacity="0.55"
      >
        <line x1="22" y1="32" x2="62" y2="52" />
        <line x1="62" y1="52" x2="96" y2="26" />
        <line x1="96" y1="26" x2="156" y2="18" />
        <line x1="62" y1="52" x2="70" y2="96" />
        <line x1="96" y1="26" x2="180" y2="42" />
        <line x1="180" y1="42" x2="220" y2="88" />
        <line x1="70" y1="96" x2="136" y2="78" />
      </g>
      <g fill="currentColor">
        {seeds.map(([x, y, r]) => (
          <circle key={`${x}-${y}`} cx={x} cy={y} r={r} />
        ))}
      </g>
    </svg>
  );
}

/**
 * Sun-and-rays — a hand-drawn sun motif with twelve rays. Used in the
 * Journey section as a "milestone" ornament.
 */
export function SunRays({ className }: OrnamentProps) {
  const angles = Array.from({ length: 12 }, (_, i) => i * 30);
  return (
    <svg
      viewBox="-50 -50 100 100"
      className={className}
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      >
        {angles.map((a) => (
          <line
            key={a}
            x1="0"
            y1="-26"
            x2="0"
            y2="-36"
            transform={`rotate(${a})`}
          />
        ))}
        <circle cx="0" cy="0" r="16" />
        <circle cx="0" cy="0" r="9" />
      </g>
      <circle cx="0" cy="0" r="3" fill="currentColor" />
    </svg>
  );
}
